import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useCommon } from '../../../contexts/CommonContext';
import { useTenant } from '../../../contexts/TenantContext';
import { usePaginatedSearch } from '../../../hooks/usePaginatedSearch';
import { useTableStates } from '../../../hooks/useTableStates';
import { useDocumentStatusPolling } from '../../../hooks/useDocumentStatusPolling';
import TableWithPagination from '../../../components/TableWithPagination/TableWithPagination';
import { BannerDocumentModel } from 'components/BannerDocumentModel';
import PageHeader from '../../../components/PageHeader/PageHeader';
import PageContainer from '../../../components/PageContainer/PageContainer';
import EmptyState from '../../../components/EmptyState/EmptyState';
import { DocumentStatusCell } from '../../../components/DocumentStatusCell';
import ImportFileModal from './ImportFileModal';
import AddUrlModal from './AddUrlModal';
import UpdateFileModal from './UpdateFileModal';
import UpdateUrlModal from './UpdateUrlModal';
import RestoreFileModal from './RestoreFileModal';
import { KnowledgeBaseApi } from '../../../api/KnowledgeBaseApi';
import { showAlert, showToast } from '../../../api/CommonApi';
import { EllipsisTypo, MoreOptionsButton, SearchInput, CellInnerGrid, CellRowGrid } from './styles';
import { Document, DocumentStatus } from '../../../models/knowledgeBase/Document';
import {
  BdsButton,
  BdsDropdown,
  BdsGrid,
  BdsIcon,
  BdsList,
  BdsListItem,
  BdsSkeleton,
  BdsTableCell,
  BdsTableRow,
  BdsTypo,
} from 'blip-ds/dist/blip-ds-react';
import { useContentLocalizer } from '../../../hooks/useContentLocalizer';
import { useDateTimeFormatter } from '../../../hooks/useDateTimeFormatter';
import { localization } from './localization';
import { calculateOffset } from '../helper';
import { useLocalizedBreadcrumbs } from '../../../utils/breadcrumbUtils';
import { getDocumentIconName, formatFileSize } from '../../../utils/fileUtil';
import { trackEvent } from '../../../api/Billing/Tracking';
import {
  KNOWLEDGE_CONTENT_ADD,
  KNOWLEDGE_CONTENT_DELETE,
  KNOWLEDGE_CONTENT_DISABLED,
  origin,
  action,
} from '../../../shared/SegmentEvents';
import { MetricStatus } from '../../../shared/BillingMetrics';
import UploadFileSidebar from './UploadFileSidebar';
import FeatureToggleService from 'services/FeatureToggleService';
import { KNOWLEDGE_BASE_DOCUMENT_PRE_PROCESSING } from 'shared/FeatureToggleKeys';
// Constants
const PAGE_OPTIONS = [5, 10, 15];

type DeleteDocumentAlertResponse = { response: boolean };

const Documents: React.FC = () => {
  const { catalogId } = useParams<{ catalogId: string }>();
  const history = useHistory();
  const {
    setHeaderContent,
    common: { language, loggedUser },
  } = useCommon();
  const { tenant } = useTenant();
  const translate = useContentLocalizer(localization);
  const addContentDropdownRef = useRef<HTMLBdsDropdownElement>(null);

  // Pagination and search hook
  const {
    searchTerm,
    debouncedSearchTerm,
    page,
    itemsPerPage,
    loadingState,
    setLoadingState,
    handleSearchChange,
    handlePaginationChange,
    handleItemsPerPageChange,
  } = usePaginatedSearch({ pageOptions: PAGE_OPTIONS });

  // Memoize the header content string to avoid dependency issues
  const headerContentText = useMemo(() => translate.headerContent, [translate.headerContent]);
  const successCatalogNameUpdatedToast = useMemo(
    () => translate.SuccessCatalogNameUpdatedToast,
    [translate.SuccessCatalogNameUpdatedToast],
  );
  const errorCatalogNameUpdateToast = useMemo(
    () => translate.errorCatalogNameUpdateToast,
    [translate.errorCatalogNameUpdateToast],
  );

  // State management
  const [documents, setDocuments] = useState<Document[]>([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [catalogName, setCatalogName] = useState('');
  const [isCatalogNameLoading, setIsCatalogNameLoading] = useState(true);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isAddUrlModalOpen, setIsAddUrlModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isUpdateUrlModalOpen, setIsUpdateUrlModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isUploadFileSidebarOpen, setIsUploadFileSidebarOpen] = useState(false);
  const [isDocumentPreProcessingEnabled, setIsDocumentPreProcessingEnabled] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [selectedDocumentForUpdate, setSelectedDocumentForUpdate] = useState<Document | null>(null);
  const [selectedDocumentForRestore, setSelectedDocumentForRestore] = useState<Document | null>(null);

  // Refs for dropdown management
  const dropdownRefs = useRef<Map<string, HTMLBdsDropdownElement>>(new Map());

  // Computed values
  const { isLoading, hasError } = loadingState;
  const tenantId = tenant?.info?.id;

  const computedStates = useTableStates({
    loadingState,
    itemsLength: documents.length,
    searchTermLength: debouncedSearchTerm.length,
  });

  const formatDateTime = useDateTimeFormatter(language, translate.at);

  useEffect(() => {
    const checkFeatures = async (): Promise<void> => {
      const isDocumentPreProcessingEnabled = (await FeatureToggleService.isFeatureToggleEnabled(KNOWLEDGE_BASE_DOCUMENT_PRE_PROCESSING)) || false;
      setIsDocumentPreProcessingEnabled(isDocumentPreProcessingEnabled);
    };
    checkFeatures();
  }, []);

  const handleDocumentsUpdate = useCallback((updatedDocuments: Document[]) => {
    setDocuments(updatedDocuments);
  }, []);

  useDocumentStatusPolling({
    documents,
    tenantId: tenantId || '',
    catalogId: catalogId || '',
    onDocumentsUpdate: handleDocumentsUpdate,
  });

  // API handlers
  const fetchDocuments = useCallback(async (): Promise<void> => {
    if (!tenantId || !catalogId || !itemsPerPage || !page) return;

    setLoadingState({ isLoading: true, hasError: false });
    try {
      const data = await KnowledgeBaseApi.getDocumentsAsync(
        tenantId,
        catalogId,
        itemsPerPage,
        calculateOffset(page, itemsPerPage),
        debouncedSearchTerm || undefined,
      );
      setDocuments(data.items);
      setTotalDocuments(data.total);
      setLoadingState({ isLoading: false, hasError: false });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching documents:', error);
      setLoadingState({ isLoading: false, hasError: true });
    }
  }, [tenantId, catalogId, itemsPerPage, page, debouncedSearchTerm]);

  const fetchCatalogInfo = useCallback(async (): Promise<void> => {
    if (!tenantId || !catalogId) return;

    setIsCatalogNameLoading(true);
    try {
      const catalog = await KnowledgeBaseApi.getCatalogByIdAsync(tenantId, catalogId);
      setCatalogName(catalog.name);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching catalog info:', error);
    } finally {
      setIsCatalogNameLoading(false);
    }
  }, [tenantId, catalogId]);
  
  const handleImportFile = useCallback((): void => {
    addContentDropdownRef.current?.setClose();

    trackEvent(KNOWLEDGE_CONTENT_ADD, tenant, loggedUser, {
      date: new Date(),
      planStatus: MetricStatus.normal,
      action: action.CLICK,
      origin: origin.KNOWLEDGEBASE,
    });

    isDocumentPreProcessingEnabled ? setIsUploadFileSidebarOpen(true): setIsImportModalOpen(true);
  }, [tenant, loggedUser, isDocumentPreProcessingEnabled]);

  const handleImportModalClose = useCallback(
    (wasCancelled: boolean): void => {
      setIsImportModalOpen(false);
      if (!wasCancelled) {
        // Refresh the documents list after successful import
        fetchDocuments();
      }
    },
    [fetchDocuments],
  );

  const handleAddUrlModalClose = useCallback(
    (wasCancelled: boolean): void => {
      setIsAddUrlModalOpen(false);
      if (!wasCancelled) {
        // Refresh the documents list after successful URL addition
        fetchDocuments();
      }
    },
    [fetchDocuments],
  );
  const handleAddUrl = useCallback((): void => {
    addContentDropdownRef.current?.setClose();

    trackEvent(KNOWLEDGE_CONTENT_ADD, tenant, loggedUser, {
      date: new Date(),
      planStatus: MetricStatus.normal,
      action: action.CLICK,
      origin: origin.KNOWLEDGEBASE,
    });

    setIsAddUrlModalOpen(true);
  }, [tenant, loggedUser]);

  const handleUpdateFile = useCallback((documentId: string): void => {
    setSelectedDocumentId(documentId);
    isDocumentPreProcessingEnabled ? setIsUploadFileSidebarOpen(true): setIsUpdateModalOpen(true);
  }, [isDocumentPreProcessingEnabled]);

  const handleUpdateUrl = useCallback((document: Document): void => {
    setSelectedDocumentForUpdate(document);
    setIsUpdateUrlModalOpen(true);
  }, []);

  const handleUpdateModalClose = useCallback(
    (wasCancelled: boolean): void => {
      setIsUpdateModalOpen(false);
      setSelectedDocumentId('');
      if (!wasCancelled) {
        // Refresh the documents list after successful file update
        fetchDocuments();
      }
    },
    [fetchDocuments],
  );

  const handleUpdateUrlModalClose = useCallback(
    (wasCancelled: boolean): void => {
      setIsUpdateUrlModalOpen(false);
      setSelectedDocumentForUpdate(null);
      if (!wasCancelled) {
        // Refresh the documents list after successful URL update
        fetchDocuments();
      }
    },
    [fetchDocuments],
  );

  const handleRestoreFile = useCallback((document: Document): void => {
    setSelectedDocumentForRestore(document);
    setIsRestoreModalOpen(true);
  }, []);

  const handleRestoreModalClose = useCallback(
    (wasCancelled: boolean): void => {
      setIsRestoreModalOpen(false);
      if (!wasCancelled) {
        // Refresh the documents list after successful file restore
        fetchDocuments();
      }
    },
    [fetchDocuments],
  );

  const handleUploadFileSidebarClose = useCallback(
    (wasCancelled: boolean): void => {
      setSelectedDocumentId('');
      setIsUploadFileSidebarOpen(false);
      if (!wasCancelled) {
        // Refresh the documents list after successful file upload
        fetchDocuments();
      }
    },
    [fetchDocuments],
  );

  const handleOpenUrlDocument = useCallback(
    (document: Document): void => {
      history.push(`/knowledge-base/${catalogId}/documents/${document.id}/url-paths`, {
        catalogName,
        documentName: document.name,
      });
    },
    [history, catalogId, catalogName],
  );

  const showDeleteDocumentAlert = useCallback(
    (): Promise<DeleteDocumentAlertResponse> =>
      showAlert({
        ...translate.deleteDocumentAlert,
        variant: 'delete',
        icon: 'trash',
      }),
    [],
  );

  const handleDeleteDocument = useCallback(
    async (documentId: string) => {
      const confirm = await showDeleteDocumentAlert();
      if (!confirm.response) return;

      try {
        setLoadingState((prev) => ({ ...prev, isLoading: true }));
        if (catalogId) {
          await KnowledgeBaseApi.deleteDocumentAsync(tenant.info.id, catalogId, documentId);
        }

        const deletedDocument = documents.find((doc) => doc.id === documentId);

        trackEvent(KNOWLEDGE_CONTENT_DELETE, tenant, loggedUser, {
          date: new Date(),
          planStatus: MetricStatus.normal,
          action: action.CLICK,
          origin: origin.KNOWLEDGEBASE,
          knowcontName: deletedDocument?.name ?? 'unknown',
          knowcontType: deletedDocument?.extension ?? 'unknown',
        });

        showToast({
          ...translate.successDeleteDocumentToast,
          type: 'success',
        });
        fetchDocuments();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting document:', error);
        setLoadingState((prev) => ({ ...prev, isLoading: false }));
        showToast({
          ...translate.errorDeleteDocumentToast,
          type: 'danger',
        });
      }
    },
    [fetchDocuments, showDeleteDocumentAlert, tenant, catalogId, documents, loggedUser],
  );

  const handleDownloadDocument = useCallback(
    async (document: Document) => {
      try {
        if (!document.url) {
          showToast({
            ...translate.errorDownloadDocumentToast,
            type: 'danger',
          });
          return;
        }

        // Refresh the media link before downloading
        const refreshedUrl = await KnowledgeBaseApi.refreshMediaExpiredLinksAsync(document.url);

        // Create a temporary anchor element to trigger download
        const link = window.document.createElement('a');
        link.href = refreshedUrl;
        link.download = document.name || 'download';
        link.target = '_blank';

        // Append to body, click, and remove
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        showToast({
          ...translate.successDownloadDocumentToast,
          type: 'success',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error downloading document:', error);
        showToast({
          ...translate.errorDownloadDocumentToast,
          type: 'danger',
        });
      }
    },
    [translate],
  );

  const handleChangeDocumentStatus = useCallback(
    async (document: Document) => {
      if (!tenantId || !catalogId) return;

      const updatedStatus = document.status === DocumentStatus.ACTIVE ? DocumentStatus.INACTIVE : DocumentStatus.ACTIVE;

      try {
        setLoadingState((prev) => ({ ...prev, isLoading: true }));

        await KnowledgeBaseApi.updateDocumentAsync(tenantId, catalogId, document.id, { status: updatedStatus });

        if (updatedStatus === DocumentStatus.INACTIVE) {
          trackEvent(KNOWLEDGE_CONTENT_DISABLED, tenant, loggedUser, {
            date: new Date(),
            planStatus: MetricStatus.normal,
            action: action.CLICK,
            origin: origin.KNOWLEDGEBASE,
            knowcontName: document.name,
            knowcontType: document.extension,
          });
        }

        showToast({
          ...(updatedStatus === DocumentStatus.ACTIVE
            ? translate.successActivateDocumentToast
            : translate.successDeactivateDocumentToast),
          type: 'success',
        });
        fetchDocuments();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error changing document status:', error);
        showToast({
          ...(updatedStatus === DocumentStatus.ACTIVE
            ? translate.errorActivateDocumentToast
            : translate.errorDeactivateDocumentToast),
          type: 'danger',
        });
      } finally {
        setLoadingState((prev) => ({ ...prev, isLoading: false }));
      }
    },
    [tenantId, catalogId, fetchDocuments, translate, tenant, loggedUser],
  );

  const handleEditCatalogName = useCallback(
    async (newName: string): Promise<void> => {
      if (!tenantId || !catalogId) return;

      setIsCatalogNameLoading(true);
      try {
        await KnowledgeBaseApi.updateCatalogAsync(tenantId, catalogId, newName);
        setCatalogName(newName);
        showToast({ ...successCatalogNameUpdatedToast, type: 'success' });
      } catch (error) {
        showToast({ ...errorCatalogNameUpdateToast, type: 'danger' });
      } finally {
        setIsCatalogNameLoading(false);
      }
    },
    [tenantId, catalogId, successCatalogNameUpdatedToast, errorCatalogNameUpdateToast],
  );

  // Effects
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    fetchCatalogInfo();
  }, [fetchCatalogInfo]);

  const skeletonElement = useMemo(() => <BdsSkeleton width="120px" height="16px" />, []);

  // Memoize breadcrumb data with stable dependencies
  const breadcrumbData = useLocalizedBreadcrumbs('knowledgeBaseDocuments', {
    catalogName,
    fallbackText: headerContentText,
    isLoading: isCatalogNameLoading,
    editHandler: catalogName && !isCatalogNameLoading ? handleEditCatalogName : undefined,
    skeletonElement,
  });

  useEffect(() => {
    if (breadcrumbData) {
      const { breadcrumbItems, lastItemEditable, onEditLastItem } = breadcrumbData;

      setHeaderContent({
        breadcrumbItems,
        lastItemEditable,
        onEditLastItem,
      });
    }
    return () => setHeaderContent({ redirect: null });
  }, [setHeaderContent, breadcrumbData]);

  // Render helpers
  const renderDocumentRow = useCallback(
    (item: Document) => {
      const closeDropdown = () => {
        const dropdown = dropdownRefs.current.get(item.id);
        dropdown?.setClose();
      };

      const setDropdownRef = (element: HTMLBdsDropdownElement | null) => {
        if (element) {
          dropdownRefs.current.set(item.id, element);
        } else {
          dropdownRefs.current.delete(item.id);
        }
      };

      return (
        <BdsTableRow key={item.id}>
          <BdsTableCell type="custom">
            <CellRowGrid direction="row" alignItems="center" gap="2">
              <BdsIcon theme="outline" name={getDocumentIconName(item.extension)} size="medium" />
              <CellInnerGrid direction="column">
                <EllipsisTypo variant="fs-14" bold="bold">
                  {item.name}
                </EllipsisTypo>
                <EllipsisTypo ghost variant="fs-12">
                  {item.extension === 'url' ? item.url : formatFileSize(item.content_size_bytes)}
                </EllipsisTypo>
              </CellInnerGrid>
            </CellRowGrid>
          </BdsTableCell>
          <BdsTableCell type="custom">
            <BdsGrid direction="column">
              <BdsTypo variant="fs-14">{formatDateTime(item.updated_at)}</BdsTypo>
              <EllipsisTypo ghost variant="fs-12">
                {`${translate.by} ${item.updated_by}`}
              </EllipsisTypo>
            </BdsGrid>
          </BdsTableCell>
          <BdsTableCell type="custom">
            <DocumentStatusCell
              status={item.status}
              processingText={translate.syncStatus.processing}
              statusTexts={translate.syncStatus}
              documentId={item.id}
            />
          </BdsTableCell>
          <BdsTableCell type="custom">
            <BdsDropdown ref={setDropdownRef} position="bottom-left">
              <div slot="dropdown-activator">
                <MoreOptionsButton name="more-options-vertical" />
              </div>
              <div slot="dropdown-content">
                <BdsList type-list="default">
                  {item.status !== DocumentStatus.PROCESSING && item.extension === 'url' && (
                    <BdsListItem
                      text={translate.documentMoreOptions.open}
                      clickable
                      icon="folder"
                      onClick={() => {
                        handleOpenUrlDocument(item);
                        closeDropdown();
                      }}
                    />
                  )}
                  {item.status !== DocumentStatus.PROCESSING && item.extension !== 'url' && (
                    <BdsListItem
                      text={translate.documentMoreOptions.download}
                      clickable
                      icon="download"
                      onClick={() => {
                        handleDownloadDocument(item);
                        closeDropdown();
                      }}
                    />
                  )}
                  {item.status !== DocumentStatus.PROCESSING && (
                    <BdsListItem
                      onClick={() => {
                        if (item.extension === 'url') {
                          handleUpdateUrl(item);
                        } else {
                          handleUpdateFile(item.id);
                        }
                        closeDropdown();
                      }}
                      text={translate.documentMoreOptions.update}
                      clickable
                      icon="upload"
                    />
                  )}
                  {item.status !== DocumentStatus.PROCESSING &&
                    Array.isArray(item.versions) &&
                    item.versions.length > 1 && (
                      <BdsListItem
                        onClick={() => {
                          handleRestoreFile(item);
                          closeDropdown();
                        }}
                        text={translate.documentMoreOptions.restore}
                        clickable
                        icon="restore"
                      />
                    )}
                  {item.status !== DocumentStatus.PROCESSING && item.status !== DocumentStatus.FAILED && (
                    <BdsListItem
                      onClick={() => {
                        handleChangeDocumentStatus(item);
                        closeDropdown();
                      }}
                      text={
                        item.status === DocumentStatus.ACTIVE
                          ? translate.documentMoreOptions.deactivate
                          : translate.documentMoreOptions.activate
                      }
                      clickable
                      icon="status"
                    />
                  )}
                  <BdsListItem
                    onClick={() => {
                      handleDeleteDocument(item.id);
                      closeDropdown();
                    }}
                    text={translate.documentMoreOptions.delete}
                    clickable
                    icon="trash"
                  />
                </BdsList>
              </div>
            </BdsDropdown>
          </BdsTableCell>
        </BdsTableRow>
      );
    },
    [
      handleDeleteDocument,
      handleUpdateFile,
      handleUpdateUrl,
      handleDownloadDocument,
      formatDateTime,
      translate,
      handleRestoreFile,
      handleChangeDocumentStatus,
    ],
  );
  const renderEmptyState = useCallback(
    () => (
      <EmptyState
        {...translate.emptyState}
        primaryButton={{
          label: translate.importFileButton,
          onClick: handleImportFile,
        }}
        outlineButton={{
          label: translate.addUrlButton,
          onClick: handleAddUrl,
        }}
        illustrationName="letter-1"
      />
    ),
    [handleImportFile, handleAddUrl, translate.emptyState, translate.importFileButton, translate.addUrlButton],
  );

  const renderTableContent = useCallback(
    () => (
      <BdsGrid direction="column" gap="4">
        <BannerDocumentModel />
        <SearchInput
          onBdsChange={handleSearchChange}
          icon="search"
          placeholder={translate.searchPlaceholder}
          value={searchTerm}
        />
        <TableWithPagination
          options={{
            heading: [
              translate.tableHeaders.content,
              translate.tableHeaders.lastUpdate,
              translate.tableHeaders.synchronization,
              '',
            ],
            columnWidths: ['auto', '13rem', '10rem', '3rem'],
            itemsPerPageList: PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: totalDocuments,
            isLoading,
            hasLoadingError: hasError,
            hasEmptyState: computedStates.hasSearchResults,
          }}
          onRefreshData={fetchDocuments}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {documents.length > 0 && documents.map(renderDocumentRow)}
        </TableWithPagination>
      </BdsGrid>
    ),
    [
      handleSearchChange,
      searchTerm,
      itemsPerPage,
      totalDocuments,
      isLoading,
      hasError,
      computedStates.hasSearchResults,
      fetchDocuments,
      handlePaginationChange,
      handleItemsPerPageChange,
      documents,
      renderDocumentRow,
    ],
  );

  // Early return for missing tenant or catalogId
  if (!tenantId || !catalogId) return null;

  return (
    <PageContainer>
      <BdsGrid direction="column" gap="4">
        <PageHeader title={translate.title} subtitle={translate.subtitle}>
          {!computedStates.hasNoItems && (
            <BdsDropdown ref={addContentDropdownRef} position="bottom-left">
              <div slot="dropdown-activator">
                <BdsButton iconRight="arrow-down">{translate.addContentButton}</BdsButton>
              </div>
              <div slot="dropdown-content">
                <BdsList type-list="default">
                  <BdsListItem onClick={handleImportFile} text={translate.importFileButton} clickable icon="file-txt" />
                  <BdsListItem onClick={handleAddUrl} text={translate.addUrlButton} clickable icon="site" />
                </BdsList>
              </div>
            </BdsDropdown>
          )}
        </PageHeader>
        {computedStates.hasNoItems ? renderEmptyState() : renderTableContent()}
      </BdsGrid>

      <ImportFileModal
        isOpen={isImportModalOpen}
        tenantId={tenantId}
        catalogId={catalogId}
        onClose={handleImportModalClose}
      />

      <AddUrlModal
        isOpen={isAddUrlModalOpen}
        tenantId={tenantId}
        catalogId={catalogId}
        onClose={handleAddUrlModalClose}
      />

      <UpdateFileModal
        isOpen={isUpdateModalOpen}
        tenantId={tenantId}
        catalogId={catalogId}
        documentId={selectedDocumentId}
        onClose={handleUpdateModalClose}
      />

      <UpdateUrlModal
        isOpen={isUpdateUrlModalOpen}
        tenantId={tenantId}
        catalogId={catalogId}
        documentId={selectedDocumentForUpdate?.id ?? ''}
        currentUrl={selectedDocumentForUpdate?.url ?? ''}
        onClose={handleUpdateUrlModalClose}
      />

      <RestoreFileModal
        isOpen={isRestoreModalOpen}
        tenantId={tenantId}
        catalogId={catalogId}
        document={selectedDocumentForRestore}
        onClose={handleRestoreModalClose}
      />

      <UploadFileSidebar
        isOpen={isUploadFileSidebarOpen}
        tenantId={tenantId}
        catalogId={catalogId}
        documentToUpdateId={selectedDocumentId}
        onClose={handleUploadFileSidebarClose}
      />
    </PageContainer>
  );
};

export default Documents;
